import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import moment from 'moment';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { MultiSelectModule } from 'primeng/multiselect';
import { ProgressBarModule } from 'primeng/progressbar';
import { RippleModule } from 'primeng/ripple';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { forkJoin, Observable } from 'rxjs';
import { Club } from '../../../../model/Club';
import { CrosselingItem } from '../../../../model/Crosseling/CrosselingItem';
import { HttpResponse } from '../../../../model/HttpResponse';
import { QueryOptionDto } from '../../../../model/QueryOptionDto';
import { AcademyAdministratorOperation } from '../../../../model/administrator/AcademyAdministratorOperation';
import { AcademyAdministratorType } from '../../../../model/administrator/AcademyAdministratorType';
import { AdministratorEntity } from '../../../../model/administrator/AdministratorEntity';
import { Category } from '../../../../model/competitions/Category';
import { Competition } from '../../../../model/competitions/Competition';
import { CategoryLevel } from '../../../../model/competitions/Level';
import { Registration } from '../../../../model/competitions/Registration';
import { RegistrationStatus } from '../../../../model/enum/RegistrationStatus';
import { RegistrationType } from '../../../../model/enum/RegistrationType';
import { TemporalRegistration } from '../../../../model/enum/TemporalRegistration';
import { Member } from '../../../../model/member';
import { ClubService } from '../../../../services/club.service';
import { CompetitionService } from '../../../../services/competition.service';
import { GestionService } from '../../../../services/gestion.service';
import { LoadingService } from '../../../../services/loading.service';
import { ExcelReport, ReportService } from '../../../../services/report.service';
import { RoleService } from '../../../../services/role.service';

export interface AdvancedRegistrationRow {
  /** Inscripción original, requerida para las operaciones de edición y eliminación */
  registration: Registration;
  isActive: boolean;
  registrationId: string;
  memberId: string;
  clubId: string;
  clubName: string;
  identification: string;
  name: string;
  lastName: string;
  fullName: string;
  birthDay: string;
  age: number | null;
  level: string;
  category: string;
  genderLabel: string;
  status: RegistrationStatus;
  statusLabel: string;
  statusSeverity: string;
  temporalType: TemporalRegistration;
  temporalTypeLabel: string;
  paymentStatus: string;
  paymentStatusLabel: string;
  paymentStatusSeverity: string;
  paymentTypeLabel: string;
  paymentId: string;
  crossSellingDescription: string;
  crossSellingAmount: number;
  registrationAmount: number;
  totalAmount: number;
  registrationDate: string;
  comments: string;
  hasMemberData: boolean;
}

export interface ClubRegistrationSummary {
  clubId: string;
  clubName: string;
  isExternal: boolean;
  email: string;
  telephone: string;
  registered: number;
  preRegistered: number;
  notRegistered: number;
  deleted: number;
  total: number;
  completionRate: number;
  clubRegistration: Registration | null;
  clubRegistrationStatus: RegistrationStatus | null;
  clubRegistrationStatusLabel: string;
  clubRegistrationStatusSeverity: string;
  clubAmount: number;
  confirmedAmount: number;
  pendingAmount: number;
  totalAmount: number;
  rows: AdvancedRegistrationRow[];
}

@Component({
  selector: 'app-advanced-registrations',
  standalone: true,
  providers: [MessageService, ConfirmationService],
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    RippleModule,
    TagModule,
    InputTextModule,
    InputTextareaModule,
    MultiSelectModule,
    DropdownModule,
    CheckboxModule,
    ProgressBarModule,
    SelectButtonModule,
    TooltipModule,
    DialogModule,
    ConfirmDialogModule,
    ToastModule,
  ],
  templateUrl: './advanced-registrations.component.html',
  styleUrl: './advanced-registrations.component.scss',
})
export class AdvancedRegistrationsComponent implements OnInit {
  @Input()
  competition!: Competition;

  /** Inscripciones de la competencia en todos sus estados: REGISTERED, PRE_REGISTERED y NOT_REGISTERED */
  @Input()
  registrations: Registration[] = [];

  @Input()
  categories: Category[] = [];

  @Input()
  levels: CategoryLevel[] = [];

  /** Notifica al detalle del evento que las inscripciones cambiaron para refrescar las demás pestañas */
  @Output()
  registrationsUpdated: EventEmitter<void> = new EventEmitter<void>();

  clubes: Club[] = [];
  members: Member[] = [];

  clubSummaries: ClubRegistrationSummary[] = [];
  filteredClubSummaries: ClubRegistrationSummary[] = [];
  allRows: AdvancedRegistrationRow[] = [];
  filteredRows: AdvancedRegistrationRow[] = [];

  expandedRows: { [key: string]: boolean } = {};
  showAdvancedView: boolean = false;
  /** Inscripciones cuyo deportista ya no existe porque el club lo eliminó de su escuela */
  orphanRows: number = 0;

  viewMode: string = 'CLUBS';
  viewModeOptions = [
    { label: 'Por Club / Escuela', value: 'CLUBS' },
    { label: 'Detalle de Inscripciones', value: 'DETAIL' },
  ];

  searchTerm: string = '';
  statusFilter: RegistrationStatus[] = [];
  clubFilter: string[] = [];
  temporalFilter: TemporalRegistration[] = [];
  paymentStatusFilter: string[] = [];
  onlyWithPendings: boolean = false;
  includeDeleted: boolean = false;

  // ======================================
  // Edición (solo super administradores)
  // ======================================
  isSuperAdmin: boolean = false;
  selectedRegistrationIds: Set<string> = new Set<string>();
  showStatusDialog: boolean = false;
  registrationsToEdit: Registration[] = [];
  registrationsToEditLabels: string[] = [];
  newStatus: RegistrationStatus | null = null;
  editComment: string = '';

  statusOptions = [
    { label: 'Inscrito', value: RegistrationStatus.REGISTERED },
    { label: 'Pre-Inscrito', value: RegistrationStatus.PRE_REGISTERED },
    { label: 'No Inscrito', value: RegistrationStatus.NOT_REGISTERED },
  ];

  temporalOptions = [
    { label: 'Ordinaria', value: TemporalRegistration.ORDINARY },
    { label: 'Extraordinaria', value: TemporalRegistration.EXTRAORDINARY },
    { label: 'Sin definir', value: TemporalRegistration.UNDEFINED },
  ];

  clubOptions: { label: string; value: string }[] = [];
  paymentStatusOptions: { label: string; value: string }[] = [];

  totals = {
    registrations: 0,
    registered: 0,
    preRegistered: 0,
    notRegistered: 0,
    clubs: 0,
    clubsWithRegistration: 0,
    confirmedAmount: 0,
    pendingAmount: 0,
  };

  private static readonly STATUS_LABELS: { [key: string]: string } = {
    [RegistrationStatus.REGISTERED]: 'Inscrito',
    [RegistrationStatus.PRE_REGISTERED]: 'Pre-Inscrito',
    [RegistrationStatus.NOT_REGISTERED]: 'No Inscrito',
  };

  private static readonly PAYMENT_STATUS_LABELS: { [key: string]: string } = {
    PAYMENT_AVAILABLE: 'Pago Disponible',
    PAYMENT_PENDING: 'Pago Pendiente',
    PAYMENT_STARTED: 'Pago Iniciado',
    PAYMENT_CONFIRMED: 'Pago Confirmado',
    PAYMENT_FAILED: 'Pago Fallido',
    PAYMENT_ABANDONED: 'Pago Abandonado',
    PAYMENT_MANUAL_VALIDATION: 'En Validación Manual',
    PAYMENT_MANUALLY_APPROVED: 'Aprobado Manualmente',
    PAYMENT_MANUALLY_REJECTED: 'Rechazado Manualmente',
    APPROVED: 'Aprobado',
    PENDING: 'Pendiente',
    CREATED: 'Creado',
    DECLINED: 'Rechazado',
    EXPIRED: 'Expirado',
    ERROR: 'Error',
  };

  private static readonly PAYMENT_TYPE_LABELS: { [key: string]: string } = {
    DIGITAL_PAYMENT: 'Pago Digital',
    MANUAL_PAYMENT: 'Pago Manual',
    UNDEFINED: 'Sin definir',
  };

  constructor(
    private clubService: ClubService,
    private gestionService: GestionService,
    private competitionService: CompetitionService,
    private reportService: ReportService,
    private loadingService: LoadingService,
    private roleService: RoleService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.isSuperAdmin = this.roleService.isEventSuperAdmin();
    this.loadAdvancedData();
  }

  loadAdvancedData() {
    this.loadingService.showLoading();
    this.clubService.getAllClubs().subscribe({
      next: (res: HttpResponse) => {
        this.clubes = res.code === 200 ? (res.response as Club[]) : [];
        this.loadMembersOfRegistrations();
      },
      error: () => {
        this.clubes = [];
        this.loadMembersOfRegistrations();
      },
    });
  }

  loadMembersOfRegistrations() {
    const membersId = Array.from(
      new Set(
        this.registrations
          .filter(
            (registration) =>
              registration.registrationType ===
                RegistrationType.MEMBER_REGISTRATION && !!registration.memberId
          )
          .map((registration) => registration.memberId as string)
      )
    );

    if (membersId.length === 0) {
      this.buildAdvancedView();
      this.loadingService.hideLoading();
      return;
    }

    const conditionals = membersId.map((id, i) => ({
      conditionalConnector: 'OR',
      logicOperator: '=',
      evaluatedProperty: 'id',
      parameterName: `@MemberId${i}`,
      parameterValue: id,
    }));

    const options: QueryOptionDto = {
      options: { conditionals: conditionals },
    };

    const entity: AdministratorEntity = {
      operation: AcademyAdministratorOperation.SELECT,
      type: AcademyAdministratorType.MEMBERS_MASSIVE,
      payload: options,
    };

    this.gestionService.sendRequest(entity).subscribe({
      next: (res: HttpResponse) => {
        this.members = res.code === 200 ? (res.response as Member[]) : [];
        this.buildAdvancedView();
        this.loadingService.hideLoading();
      },
      error: () => {
        this.members = [];
        this.buildAdvancedView();
        this.loadingService.hideLoading();
      },
    });
  }

  buildAdvancedView() {
    this.allRows = this.registrations
      .filter(
        (registration) =>
          registration.registrationType ===
            RegistrationType.MEMBER_REGISTRATION &&
          (this.includeDeleted || registration.isActive !== false)
      )
      .map((registration) => this.buildRow(registration));

    this.clubSummaries = this.buildClubSummaries();

    this.clubOptions = this.clubSummaries.map((summary) => ({
      label: summary.clubName,
      value: summary.clubId,
    }));

    this.paymentStatusOptions = Array.from(
      new Set(this.allRows.map((row) => row.paymentStatus).filter((s) => !!s))
    ).map((status) => ({
      label: this.getPaymentStatusLabel(status),
      value: status,
    }));

    this.applyFilters();
    this.showAdvancedView = true;
  }

  private buildRow(registration: Registration): AdvancedRegistrationRow {
    const member = this.members.find((m) => m.id === registration.memberId);
    const club = this.clubes.find((c) => c.id === registration.clubId);
    const category = member ? this.getCategory(member) : undefined;
    const level = category
      ? this.levels.find((l: CategoryLevel) => l.id === category.idCategoryLevel)
      : undefined;
    const crossSellingItems =
      registration.customData?.crosselingItems?.filter(
        (item: CrosselingItem) => item.isIncluded
      ) ?? [];
    const crossSellingAmount = crossSellingItems.reduce(
      (total: number, item: CrosselingItem) => total + (item.amount ?? 0),
      0
    );
    const registrationAmount = this.getAthleteAmount(registration);
    const paymentStatus = (
      registration.payment?.paymentStatus ?? ''
    ).toUpperCase();

    return {
      registration: registration,
      isActive: registration.isActive !== false,
      registrationId: registration.id ?? '',
      memberId: registration.memberId ?? '',
      clubId: registration.clubId,
      clubName: club?.name ?? member?.club?.name ?? 'Club no identificado',
      identification: member ? `${member.idType}${member.idNumber}` : 'N/D',
      name: member?.name?.toUpperCase() ?? 'DEPORTISTA ELIMINADO POR EL CLUB',
      lastName: member?.lastName?.toUpperCase() ?? '',
      fullName: member
        ? `${member.name} ${member.lastName}`.toUpperCase()
        : 'DEPORTISTA ELIMINADO POR EL CLUB',
      birthDay: member?.birthDay ?? 'N/D',
      age: member ? this.getCompetitionAge(member.birthDay) : null,
      level: (level?.name ?? member?.nivel ?? 'N/D') as string,
      category: category?.name ?? 'Sin categoría',
      genderLabel: member
        ? member.gender === 'M'
          ? 'MASCULINA'
          : 'FEMENINA'
        : 'N/D',
      status: registration.status,
      statusLabel: this.getStatusLabel(registration.status),
      statusSeverity: this.getStatusSeverity(registration.status),
      temporalType: registration.temporalType,
      temporalTypeLabel: this.getTemporalLabel(registration.temporalType),
      paymentStatus: paymentStatus,
      paymentStatusLabel: this.getPaymentStatusLabel(paymentStatus),
      paymentStatusSeverity: this.getPaymentStatusSeverity(paymentStatus),
      paymentTypeLabel: this.getPaymentTypeLabel(
        registration.payment?.paymentType
      ),
      paymentId: registration.payment?.paymentId ?? '',
      crossSellingDescription: crossSellingItems
        .map(
          (item: CrosselingItem) =>
            `${item.name} ($${(item.amount ?? 0).toLocaleString()})`
        )
        .join(', '),
      crossSellingAmount: crossSellingAmount,
      registrationAmount: registrationAmount,
      totalAmount: registrationAmount + crossSellingAmount,
      registrationDate: registration.auditData?.createdDate
        ? moment(registration.auditData.createdDate).format('YYYY-MM-DD HH:mm')
        : 'N/D',
      comments: registration.comments ?? '',
      hasMemberData: !!member,
    };
  }

  private buildClubSummaries(): ClubRegistrationSummary[] {
    const clubIds = Array.from(
      new Set(
        this.registrations
          .filter(
            (registration) =>
              this.includeDeleted || registration.isActive !== false
          )
          .map((registration) => registration.clubId)
      )
    );

    return clubIds
      .map((clubId) => {
        const club = this.clubes.find((c) => c.id === clubId);
        const rows = this.allRows.filter((row) => row.clubId === clubId);
        const clubRegistration = this.registrations.find(
          (registration) =>
            registration.clubId === clubId &&
            registration.registrationType ===
              RegistrationType.CLUB_REGISTRATION &&
            registration.isActive !== false
        );
        const activeRows = rows.filter((row) => row.isActive);
        const registered = activeRows.filter(
          (row) => row.status === RegistrationStatus.REGISTERED
        );
        const preRegistered = activeRows.filter(
          (row) => row.status === RegistrationStatus.PRE_REGISTERED
        );
        const notRegistered = activeRows.filter(
          (row) => row.status === RegistrationStatus.NOT_REGISTERED
        );
        const clubAmount = clubRegistration
          ? this.getClubAmount(clubRegistration)
          : 0;
        const confirmedAmount =
          registered.reduce((total, row) => total + row.totalAmount, 0) +
          (clubRegistration?.status === RegistrationStatus.REGISTERED
            ? clubAmount
            : 0);
        const pendingAmount =
          preRegistered.reduce((total, row) => total + row.totalAmount, 0) +
          (clubRegistration &&
          clubRegistration.status !== RegistrationStatus.REGISTERED
            ? clubAmount
            : 0);

        return {
          clubId: clubId,
          clubName:
            club?.name ?? rows[0]?.clubName ?? `Club sin nombre (${clubId})`,
          isExternal: club?.administrativeInformation?.isExternal === true,
          email: club?.contactInformation?.email ?? '',
          telephone: club?.contactInformation?.telephone ?? '',
          registered: registered.length,
          preRegistered: preRegistered.length,
          notRegistered: notRegistered.length,
          deleted: rows.length - activeRows.length,
          total: activeRows.length,
          completionRate: activeRows.length
            ? Math.round((registered.length / activeRows.length) * 100)
            : 0,
          clubRegistration: clubRegistration ?? null,
          clubRegistrationStatus: clubRegistration?.status ?? null,
          clubRegistrationStatusLabel: clubRegistration
            ? this.getStatusLabel(clubRegistration.status)
            : 'Sin inscripción de club',
          clubRegistrationStatusSeverity: clubRegistration
            ? this.getStatusSeverity(clubRegistration.status)
            : 'info',
          clubAmount: clubAmount,
          confirmedAmount: confirmedAmount,
          pendingAmount: pendingAmount,
          totalAmount: confirmedAmount + pendingAmount,
          rows: rows,
        };
      })
      .sort((a, b) => a.clubName.localeCompare(b.clubName));
  }

  applyFilters() {
    const term = this.searchTerm.trim().toLowerCase();

    const matchesRow = (row: AdvancedRegistrationRow): boolean => {
      if (this.statusFilter.length && !this.statusFilter.includes(row.status)) {
        return false;
      }
      if (
        this.temporalFilter.length &&
        !this.temporalFilter.includes(row.temporalType)
      ) {
        return false;
      }
      if (
        this.paymentStatusFilter.length &&
        !this.paymentStatusFilter.includes(row.paymentStatus)
      ) {
        return false;
      }
      if (!term) {
        return true;
      }
      return [
        row.clubName,
        row.identification,
        row.fullName,
        row.category,
        row.level,
        row.statusLabel,
        row.paymentStatusLabel,
        row.paymentId,
      ]
        .join(' ')
        .toLowerCase()
        .includes(term);
    };

    const hasRowFilters =
      !!term ||
      this.statusFilter.length > 0 ||
      this.temporalFilter.length > 0 ||
      this.paymentStatusFilter.length > 0;

    this.filteredClubSummaries = this.clubSummaries
      .filter(
        (summary) =>
          !this.clubFilter.length || this.clubFilter.includes(summary.clubId)
      )
      .filter((summary) => !this.onlyWithPendings || summary.preRegistered > 0)
      .map((summary) => ({ ...summary, rows: summary.rows.filter(matchesRow) }))
      .filter((summary) => !hasRowFilters || summary.rows.length > 0);

    this.filteredRows = this.filteredClubSummaries.reduce(
      (rows: AdvancedRegistrationRow[], summary) => rows.concat(summary.rows),
      []
    );

    this.calculateTotals();
  }

  private calculateTotals() {
    const rows = this.filteredRows;
    this.orphanRows = rows.filter((row) => !row.hasMemberData).length;
    this.totals = {
      registrations: rows.length,
      registered: rows.filter(
        (row) => row.status === RegistrationStatus.REGISTERED
      ).length,
      preRegistered: rows.filter(
        (row) => row.status === RegistrationStatus.PRE_REGISTERED
      ).length,
      notRegistered: rows.filter(
        (row) => row.status === RegistrationStatus.NOT_REGISTERED
      ).length,
      clubs: this.filteredClubSummaries.length,
      clubsWithRegistration: this.filteredClubSummaries.filter(
        (summary) =>
          summary.clubRegistrationStatus === RegistrationStatus.REGISTERED
      ).length,
      confirmedAmount: this.filteredClubSummaries.reduce(
        (total, summary) => total + summary.confirmedAmount,
        0
      ),
      pendingAmount: this.filteredClubSummaries.reduce(
        (total, summary) => total + summary.pendingAmount,
        0
      ),
    };
  }

  clearFilters() {
    this.searchTerm = '';
    this.statusFilter = [];
    this.clubFilter = [];
    this.temporalFilter = [];
    this.paymentStatusFilter = [];
    this.onlyWithPendings = false;
    this.applyFilters();
  }

  filterByStatus(status: RegistrationStatus) {
    this.statusFilter = this.statusFilter.includes(status) ? [] : [status];
    this.applyFilters();
  }

  onIncludeDeletedChange() {
    this.clearSelection();
    this.buildAdvancedView();
  }

  // ======================================
  // ✅ SELECCIÓN DE REGISTROS
  // ======================================
  isSelected(row: AdvancedRegistrationRow): boolean {
    return this.selectedRegistrationIds.has(row.registrationId);
  }

  toggleRowSelection(row: AdvancedRegistrationRow, selected: boolean) {
    if (selected) {
      this.selectedRegistrationIds.add(row.registrationId);
    } else {
      this.selectedRegistrationIds.delete(row.registrationId);
    }
  }

  isClubFullySelected(club: ClubRegistrationSummary): boolean {
    return (
      club.rows.length > 0 &&
      club.rows.every((row) => this.selectedRegistrationIds.has(row.registrationId))
    );
  }

  toggleClubSelection(club: ClubRegistrationSummary, selected: boolean) {
    club.rows.forEach((row) => this.toggleRowSelection(row, selected));
  }

  get areAllFilteredRowsSelected(): boolean {
    return (
      this.filteredRows.length > 0 &&
      this.filteredRows.every((row) =>
        this.selectedRegistrationIds.has(row.registrationId)
      )
    );
  }

  toggleAllFilteredRows(selected: boolean) {
    this.filteredRows.forEach((row) => this.toggleRowSelection(row, selected));
  }

  clearSelection() {
    this.selectedRegistrationIds.clear();
  }

  get selectedCount(): number {
    return this.selectedRegistrationIds.size;
  }

  private getSelectedRows(): AdvancedRegistrationRow[] {
    return this.allRows.filter((row) =>
      this.selectedRegistrationIds.has(row.registrationId)
    );
  }

  // ======================================
  // ✏️ EDICIÓN DE ESTADO
  // ======================================
  openStatusDialogForRow(row: AdvancedRegistrationRow) {
    this.openStatusDialog([row.registration], [`${row.fullName} (${row.identification})`]);
  }

  openStatusDialogForClub(club: ClubRegistrationSummary) {
    if (!club.clubRegistration) {
      return;
    }
    this.openStatusDialog(
      [club.clubRegistration],
      [`Inscripción de club: ${club.clubName}`]
    );
  }

  openStatusDialogForSelection() {
    const rows = this.getSelectedRows();
    if (rows.length === 0) {
      return;
    }
    this.openStatusDialog(
      rows.map((row) => row.registration),
      rows.map((row) => `${row.fullName} (${row.identification})`)
    );
  }

  private openStatusDialog(registrations: Registration[], labels: string[]) {
    if (!this.isSuperAdmin) {
      return;
    }
    this.registrationsToEdit = registrations;
    this.registrationsToEditLabels = labels;
    this.newStatus =
      registrations.length === 1 ? registrations[0].status : null;
    this.editComment = '';
    this.showStatusDialog = true;
  }

  closeStatusDialog() {
    this.showStatusDialog = false;
    this.registrationsToEdit = [];
    this.registrationsToEditLabels = [];
    this.newStatus = null;
    this.editComment = '';
  }

  confirmStatusChange() {
    if (!this.newStatus || this.registrationsToEdit.length === 0) {
      return;
    }
    const status = this.newStatus;
    const comment = this.editComment.trim();

    this.persistChanges(
      this.registrationsToEdit,
      (registration) => {
        registration.status = status;
        if (comment) {
          registration.comments = comment;
        }
      },
      `Se actualizó el estado de ${this.registrationsToEdit.length} inscripción(es) a "${this.getStatusLabel(status)}"`
    );
    this.closeStatusDialog();
  }

  // ======================================
  // 🗑️ ELIMINACIÓN LÓGICA / RESTAURACIÓN
  // ======================================
  deleteRow(row: AdvancedRegistrationRow) {
    this.requestDeletion(
      [row.registration],
      `¿Eliminar la inscripción de ${row.fullName} (${row.identification})?`
    );
  }

  deleteClubRegistration(club: ClubRegistrationSummary) {
    if (!club.clubRegistration) {
      return;
    }
    this.requestDeletion(
      [club.clubRegistration],
      `¿Eliminar la inscripción del club ${club.clubName}? Los deportistas del club conservan sus inscripciones.`
    );
  }

  deleteSelection() {
    const rows = this.getSelectedRows();
    if (rows.length === 0) {
      return;
    }
    this.requestDeletion(
      rows.map((row) => row.registration),
      `¿Eliminar las ${rows.length} inscripciones seleccionadas?`
    );
  }

  private requestDeletion(registrations: Registration[], message: string) {
    if (!this.isSuperAdmin) {
      return;
    }
    this.confirmationService.confirm({
      header: 'Confirmar eliminación',
      message: `${message} Los registros se marcan como eliminados y dejan de contar en los reportes; puedes recuperarlos activando "Ver eliminadas".`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, eliminar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.persistChanges(
          registrations,
          (registration) => {
            registration.isActive = false;
          },
          `Se eliminaron ${registrations.length} inscripción(es)`
        );
      },
    });
  }

  restoreRow(row: AdvancedRegistrationRow) {
    if (!this.isSuperAdmin) {
      return;
    }
    this.persistChanges(
      [row.registration],
      (registration) => {
        registration.isActive = true;
      },
      `Se restauró la inscripción de ${row.fullName}`
    );
  }

  // ======================================
  // 💾 PERSISTENCIA
  // ======================================
  private persistChanges(
    registrations: Registration[],
    mutate: (registration: Registration) => void,
    successDetail: string
  ) {
    const userName = this.roleService.getUserName();
    const modifiedDate = moment().format('YYYY-MM-DD HH:mm:ss');

    const updates: Observable<HttpResponse>[] = registrations.map(
      (registration) => {
        const payload: Registration = {
          ...registration,
          auditData: {
            ...registration.auditData,
            modifiedBy: userName,
            modifiedDate: modifiedDate,
          },
        };
        mutate(payload);

        const entity: AdministratorEntity = {
          operation: AcademyAdministratorOperation.UPDATE,
          type: AcademyAdministratorType.REGISTRATIONS,
          payload: payload,
        };
        return this.competitionService.sendRequest(entity);
      }
    );

    this.loadingService.showLoading();
    forkJoin(updates).subscribe({
      next: (responses: HttpResponse[]) => {
        const failed = responses.filter((response) => response.code !== 200);

        responses.forEach((response, index) => {
          if (response.code === 200) {
            // Se replica el cambio en la inscripción original para refrescar la vista sin recargar
            const registration = registrations[index];
            registration.auditData = {
              ...registration.auditData,
              modifiedBy: userName,
              modifiedDate: modifiedDate,
            };
            mutate(registration);
          }
        });

        this.clearSelection();
        this.buildAdvancedView();
        this.registrationsUpdated.emit();
        this.loadingService.hideLoading();

        if (failed.length === 0) {
          this.messageService.add({
            severity: 'success',
            summary: 'Inscripciones actualizadas',
            detail: successDetail,
            life: 4000,
          });
        } else {
          this.messageService.add({
            severity: 'warn',
            summary: 'Actualización parcial',
            detail: `${responses.length - failed.length} de ${
              responses.length
            } inscripciones se actualizaron correctamente. Revisa las restantes e inténtalo nuevamente.`,
            life: 6000,
          });
        }
      },
      error: () => {
        this.loadingService.hideLoading();
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail:
            'No fue posible actualizar las inscripciones, inténtalo nuevamente. Si el error persiste contacta al administrador del sistema.',
          life: 6000,
        });
      },
    });
  }

  expandAll() {
    this.expandedRows = this.filteredClubSummaries.reduce(
      (expanded: { [key: string]: boolean }, summary) => {
        expanded[summary.clubId] = true;
        return expanded;
      },
      {}
    );
  }

  collapseAll() {
    this.expandedRows = {};
  }

  /** Número de columnas de cada tabla, usado por los mensajes vacíos y las filas expandidas */
  get clubColumnsCount(): number {
    return this.isSuperAdmin ? 12 : 10;
  }

  get memberColumnsCount(): number {
    return (
      14 +
      (this.competition.hasCrosselling ? 1 : 0) +
      (this.isSuperAdmin ? 2 : 0)
    );
  }

  get detailColumnsCount(): number {
    return (
      15 +
      (this.competition.hasCrosselling ? 1 : 0) +
      (this.isSuperAdmin ? 2 : 0)
    );
  }

  get hasActiveFilters(): boolean {
    return (
      !!this.searchTerm.trim() ||
      this.statusFilter.length > 0 ||
      this.clubFilter.length > 0 ||
      this.temporalFilter.length > 0 ||
      this.paymentStatusFilter.length > 0 ||
      this.onlyWithPendings
    );
  }

  getCompetitionAge(birthDay: string): number {
    return moment(this.competition.baseDateToCalculateAge).diff(
      birthDay,
      'years'
    );
  }

  getCategory(member: Member): Category | undefined {
    const age = this.getCompetitionAge(member.birthDay);
    const level = this.levels.find(
      (level: CategoryLevel) => level.name === member.nivel
    );
    return this.categories.find(
      (category: Category) =>
        category.minYearsOld <= age &&
        category.maxYearsOld >= age &&
        category.idCategoryLevel === level?.id
    );
  }

  private getAthleteAmount(registration: Registration): number {
    const athleteAmount =
      registration.temporalType === TemporalRegistration.EXTRAORDINARY
        ? this.competition.registerAmounts?.extraordinary?.athlete
        : this.competition.registerAmounts?.ordinary?.athlete;

    if (typeof athleteAmount === 'number') {
      return athleteAmount;
    }
    return athleteAmount?.length ? athleteAmount[0].amount : 0;
  }

  private getClubAmount(registration: Registration): number {
    return registration.temporalType === TemporalRegistration.EXTRAORDINARY
      ? this.competition.registerAmounts?.extraordinary?.club ?? 0
      : this.competition.registerAmounts?.ordinary?.club ?? 0;
  }

  getStatusLabel(status: RegistrationStatus | string): string {
    return AdvancedRegistrationsComponent.STATUS_LABELS[status] ?? status;
  }

  getStatusSeverity(status: RegistrationStatus | string): string {
    switch (status) {
      case RegistrationStatus.REGISTERED:
        return 'success';
      case RegistrationStatus.PRE_REGISTERED:
        return 'warning';
      default:
        return 'danger';
    }
  }

  getPaymentStatusLabel(paymentStatus: string): string {
    if (!paymentStatus) {
      return 'Sin información de pago';
    }
    return (
      AdvancedRegistrationsComponent.PAYMENT_STATUS_LABELS[paymentStatus] ??
      paymentStatus
    );
  }

  getPaymentStatusSeverity(paymentStatus: string): string {
    switch (paymentStatus) {
      case 'PAYMENT_CONFIRMED':
      case 'PAYMENT_MANUALLY_APPROVED':
      case 'APPROVED':
        return 'success';
      case 'PAYMENT_PENDING':
      case 'PAYMENT_STARTED':
      case 'PAYMENT_MANUAL_VALIDATION':
      case 'PENDING':
        return 'warning';
      case 'PAYMENT_FAILED':
      case 'PAYMENT_ABANDONED':
      case 'PAYMENT_MANUALLY_REJECTED':
      case 'DECLINED':
      case 'ERROR':
      case 'EXPIRED':
        return 'danger';
      default:
        return 'info';
    }
  }

  getTemporalLabel(temporalType: TemporalRegistration | string): string {
    switch (temporalType) {
      case TemporalRegistration.ORDINARY:
        return 'Ordinaria';
      case TemporalRegistration.EXTRAORDINARY:
        return 'Extraordinaria';
      default:
        return 'Sin definir';
    }
  }

  getPaymentTypeLabel(paymentType?: string): string {
    if (!paymentType) {
      return 'Sin definir';
    }
    return (
      AdvancedRegistrationsComponent.PAYMENT_TYPE_LABELS[paymentType] ??
      paymentType
    );
  }

  exportAdvancedReport() {
    const clubsSheet = this.filteredClubSummaries.map(
      (summary: ClubRegistrationSummary) => ({
        'Club / Escuela': summary.clubName,
        Externo: summary.isExternal ? 'Si' : 'No',
        Correo: summary.email,
        Telefono: summary.telephone,
        'Inscripción Club': summary.clubRegistrationStatusLabel,
        'Total Deportistas': summary.total,
        Inscritos: summary.registered,
        'Pre-Inscritos': summary.preRegistered,
        'No Inscritos': summary.notRegistered,
        '% Inscripción Efectiva': `${summary.completionRate}%`,
        'Valor Confirmado': summary.confirmedAmount,
        'Valor Pendiente': summary.pendingAmount,
        'Valor Total': summary.totalAmount,
      })
    );

    const detailSheet = this.filteredRows.map((row: AdvancedRegistrationRow) => {
      const detail: any = {
        'Club / Escuela': row.clubName,
        Identificación: row.identification,
        Nombres: row.name,
        Apellidos: row.lastName,
        'Fecha Nacimiento': row.birthDay,
        'Edad (Corte)': row.age ?? 'N/D',
        Nivel: row.level,
        Categoría: row.category,
        Rama: row.genderLabel,
        'Deportista Existe': row.hasMemberData ? 'Si' : 'No',
        Estado: row.statusLabel,
        'Tipo Inscripción': row.temporalTypeLabel,
        'Estado Pago': row.paymentStatusLabel,
        'Medio de Pago': row.paymentTypeLabel,
        'Referencia Pago': row.paymentId,
        'Fecha Registro': row.registrationDate,
      };

      if (this.competition.hasCrosselling) {
        detail['Venta Cruzada'] = row.crossSellingDescription || 'N/A';
        detail['Valor Venta Cruzada'] = row.crossSellingAmount;
      }

      detail['Valor Inscripción'] = row.registrationAmount;
      detail['Valor Total'] = row.totalAmount;
      detail['Observaciones'] = row.comments;

      return detail;
    });

    const statusSheet = this.statusOptions.map((option) => {
      const rows = this.filteredRows.filter(
        (row) => row.status === option.value
      );
      return {
        Estado: option.label,
        'Cantidad Deportistas': rows.length,
        'Clubes Involucrados': new Set(rows.map((row) => row.clubId)).size,
        'Valor Asociado': rows.reduce(
          (total, row) => total + row.totalAmount,
          0
        ),
      };
    });

    const reports: ExcelReport[] = [
      { data: clubsSheet, sheetName: 'Resumen por Club' },
      { data: detailSheet, sheetName: 'Detalle Inscripciones' },
      { data: statusSheet, sheetName: 'Resumen por Estado' },
    ];

    const reportName = `Vista Avanzada Inscripciones ${
      this.competition.name
    } ${moment().format('DD-MM-YYYY')}`;

    this.reportService.exportExcel(reports, reportName);
  }
}
